import {
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { accountIdSelectedAtom, accountsAtom, accountSelectedSelector } from 'atoms';
import { Search, KeyboardArrowDown } from '@mui/icons-material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Account } from 'types/Account';
import AccountAvatar from 'components/AccountAvatar';
import RequirePermission from 'components/RequirePermission';
import { removeAccents } from 'services/utils';

function LateralMenu() {
  const accountSelected = useRecoilValue(accountSelectedSelector);

  return (
    <Paper variant="outlined" sx={{ width: '255px', pt: 4, flex: '0 0 auto' }}>
      <Box sx={{ px: 2 }}>
        <AccountAvatar
          image={accountSelected?.custom?.image}
          selectedColor={accountSelected?.custom?.color}
          accountName={accountSelected?.displayName || ''}
        />
        <SelectAccount {...{ accountSelected }} />
      </Box>
      <LateralMenuItems {...{ accountSelected }} />
    </Paper>
  );
}

function SelectAccount({ accountSelected }: { accountSelected: Account | null }) {
  const { t } = useTranslation('_common');

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');

  const setAccountIdSelected = useSetRecoilState(accountIdSelectedAtom);
  const accounts = useRecoilValue(accountsAtom);
  const selectEnabled = Number(accounts?.length) > 1;

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (selectEnabled) {
        setAnchorEl(event.currentTarget);
        setOpened(true);
      }
    },
    [selectEnabled],
  );

  const handleClose = useCallback(() => {
    setOpened(false);
  }, []);

  const handleSelect = useCallback(
    (id: number | null) => {
      handleClose();
      setAccountIdSelected(id);
      setSearch('');
    },
    [setAccountIdSelected, handleClose],
  );

  const filteredAccounts = useMemo(() => {
    if (search) {
      const searchText = removeAccents(search.trim().toLowerCase());
      if (searchText.length) {
        return accounts?.filter((al) => {
          return (
            removeAccents(String(al?.displayName?.toLowerCase())).includes(searchText) ||
            String(al.accountCode?.toLowerCase()).includes(searchText)
          );
        });
      }
    }
    return accounts;
  }, [search, accounts]);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        sx={{ mt: 3, mb: 1.5, cursor: selectEnabled ? 'pointer' : 'auto' }}
        onClick={handleOpen}
      >
        <Box display="flex" flexDirection="column" flex="1 1 auto" overflow="hidden">
          <Typography variant="h6" noWrap>
            {accountSelected?.displayName || t('overview')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {accountSelected?.accountCode || t('all_accounts')}
          </Typography>
        </Box>
        {selectEnabled && (
          <Box>
            <KeyboardArrowDown sx={{ flex: '0 0 auto' }} />
          </Box>
        )}
      </Box>

      <Menu
        id="accounts-menu"
        anchorEl={anchorEl}
        open={opened}
        onClose={handleClose}
        sx={{ maxHeight: '350px' }}
      >
        {Number(accounts?.length) > 5 && (
          <Box sx={{ mx: 1, mb: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('search_account')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ color: '#000000DE' }}>
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                '.MuiOutlinedInput-root': {
                  background: 'rgba(67, 31, 31, 0.08)',
                },
                '.MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            />
          </Box>
        )}

        {!search && (
          <MenuItem key={'all'} onClick={() => handleSelect(null)}>
            {`${t('overview')} (${t('all_accounts')})`}
          </MenuItem>
        )}

        {filteredAccounts?.length ? (
          filteredAccounts?.map((account) => (
            <MenuItem key={account.id} onClick={() => handleSelect(account.id)}>
              {`${account.displayName} (${account.accountCode})`}
            </MenuItem>
          ))
        ) : (
          <MenuItem key="none">{t('no_results')}</MenuItem>
        )}
      </Menu>
    </>
  );
}

function LateralMenuItems({ accountSelected }: { accountSelected: Account | null }) {
  const { t } = useTranslation('_common');
  const isAllAccounts = !accountSelected;

  return (
    <List>
      {!isAllAccounts && <MenuLink text={t('place')} path="/" />}
      {isAllAccounts && <MenuLink text={t('summary')} path="/summary" />}
      <MenuLink text={t('events')} path={`/events`} />
      <RequirePermission
        validatePermission={(account) => !!account?.showContacts || !!account?.showUsers}
      >
        <MenuLink text={t('contacts')} path={`/contacts`} />
      </RequirePermission>
      <RequirePermission validatePermission={(account) => !!account?.showZones}>
        <MenuLink text={t('zones')} path={`/zones`} />
      </RequirePermission>
      <RequirePermission validatePermission={(account) => !!account?.hasPermissionToViewCameras}>
        <MenuLink text={t('cameras')} path={`/cameras`} />
      </RequirePermission>
      <RequirePermission
        validatePermission={(account) => !!account?.hasPermissionToViewSO || isAllAccounts}
      >
        <MenuLink text={t('service_orders')} path={`/service-orders`} />
      </RequirePermission>

      <RequirePermission validatePermission={(account) => !!account?.showTimeTable}>
        <MenuLink text={t('timetable')} path={`/timetable`} />
      </RequirePermission>
    </List>
  );
}

function MenuLink({ text, path, disabled }: { text: string; path: string; disabled?: boolean }) {
  const renderLink = React.useMemo(
    () =>
      React.forwardRef<HTMLAnchorElement, Omit<NavLinkProps, 'to'>>(function Link(props, ref) {
        return (
          <NavLink
            {...props}
            ref={ref}
            to={path}
            // @ts-ignore
            className={({ isActive }) =>
              isActive ? props.className + ' Mui-selected' : props.className
            }
          />
        );
      }),
    [path],
  );

  return (
    <ListItem sx={{ p: 0 }}>
      <ListItemButton disabled={disabled} sx={{ py: 1.5, px: 2 }} component={renderLink}>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );
}

export default LateralMenu;
